import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  type Counterparty,
  type CounterpartyListCounterpartiesData,
  counterpartyDeleteCounterparty,
  counterpartyListCounterpartiesOptions,
} from '@column/api';
import { Button, Dropdown, Icon, Table, TableColumn, TableVariant, Toggle, Tooltip } from '@column/column-ui-kit';

import { PopoverFilterCreatedDate } from '~/app/pages/BankAccounts/_components/PopoverFilterCreatedDate';
import { convertOptionalDatesToRange } from '~/app/pages/BankAccounts/Overview';
import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { EmptyState, PageHeader, PopoverFilterTransferDateData, PopoverFilterWrapper } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Search } from '~/components/Search';
import { AbsoluteTime, RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Truncate } from '~/styles';

import { CounterpartyFiltersModal } from './_components/CounterpartyFiltersModal';
import { PopoverFilterText } from './_components/PopoverFilterText';

type SortSelection = '' | 'created_at:desc' | 'created_at:asc';
type DeduplicateSelection = 'true' | 'false';

type CounterpartyListQuery = NonNullable<CounterpartyListCounterpartiesData['query']>;

interface CounterpartyFilters {
  accountNumber: string;
  routingNumber: string;
  query: string;
  sort: SortSelection;
  deduplicate: DeduplicateSelection;
  date: Partial<PopoverFilterTransferDateData>;
}

const EMPTY_FILTERS: CounterpartyFilters = {
  accountNumber: '',
  routingNumber: '',
  query: '',
  sort: '',
  deduplicate: 'true',
  date: {},
};

const DEFAULT_SORT: SortSelection = 'created_at:desc';

const isSortSelection = (value: string): value is SortSelection =>
  value === '' || value === 'created_at:desc' || value === 'created_at:asc';

// Read directly from URLSearchParams so numeric-looking identifiers
// (routing / account numbers) keep their leading zeros.
const filtersFromSearchParams = (params: URLSearchParams): CounterpartyFilters => {
  const sortRaw = params.get('sort') ?? '';
  const sort = isSortSelection(sortRaw) ? sortRaw : '';
  // `deduplicate` defaults to true; only treat the explicit string "false" as off.
  const deduplicate: DeduplicateSelection = params.get('deduplicate') === 'false' ? 'false' : 'true';
  return {
    accountNumber: params.get('account_number') ?? '',
    routingNumber: params.get('routing_number') ?? '',
    query: params.get('query') ?? '',
    sort,
    deduplicate,
    date: {
      createdAtGte: params.get('created.gte') ?? undefined,
      createdAtLt: params.get('created.lt') ?? undefined,
    },
  };
};

const filtersToSearchParams = (filters: CounterpartyFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.accountNumber) params.set('account_number', filters.accountNumber);
  if (filters.routingNumber) params.set('routing_number', filters.routingNumber);
  if (filters.query) params.set('query', filters.query);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.deduplicate === 'false') params.set('deduplicate', 'false');
  if (filters.date.createdAtGte) params.set('created.gte', filters.date.createdAtGte);
  if (filters.date.createdAtLt) params.set('created.lt', filters.date.createdAtLt);
  return params;
};

const SORT_OPTIONS: { label: string; value: SortSelection }[] = [
  { label: 'Created (newest to oldest)', value: 'created_at:desc' },
  { label: 'Created (oldest to newest)', value: 'created_at:asc' },
];

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 var(--page-margin) 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const CompactFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const CounterpartySearch = styled(Search)`
  max-width: 100%;
  transition: width 0.2s;

  label {
    flex-grow: 1;
    min-width: 0;
  }

  input {
    min-width: 0;
  }
`;

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

const DeduplicateToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
`;

const DeduplicateToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(900)};
`;

const CreatedTimeCell = styled(Table.Cell)`
  position: relative;
  overflow: visible;

  &:hover {
    z-index: 50;
  }

  & > div {
    overflow: visible;
  }

  & > div > div:last-child {
    z-index: 60;
  }
`;

const buildQuery = (
  filters: CounterpartyFilters,
  searchEnabled: boolean,
  pageSize: number,
  cursor?: string
): CounterpartyListQuery => {
  const query: CounterpartyListQuery = {
    limit: pageSize,
  };

  if (cursor) query.starting_after = cursor;
  if (filters.accountNumber) query.account_number = filters.accountNumber;
  if (filters.routingNumber) query.routing_number = filters.routingNumber;
  if (filters.date.createdAtGte) query['created.gte'] = filters.date.createdAtGte;
  if (filters.date.createdAtLt) query['created.lt'] = filters.date.createdAtLt;

  if (searchEnabled) {
    if (filters.query) query.query = filters.query;
    query.deduplicate = filters.deduplicate === 'true';
    const effectiveSort = filters.sort || (filters.query ? '' : DEFAULT_SORT);
    if (effectiveSort) {
      const [sort, sortOrder] = effectiveSort.split(':');
      query.sort = sort;
      query.sort_order = sortOrder;
    }
  } else if (filters.query) {
    // Pre-flag: reuse the single text box as a description substring search.
    query.description = filters.query;
  }

  return query;
};

const counterpartyDisplayName = (entry: Counterparty): string =>
  entry.description || entry.name || entry.wire?.beneficiary_name || '-';

export const PageCounterparties: React.FC = () => {
  const { openAlert } = useDialog();
  const { currentUser, currentPlatform } = useSessionStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('counterparties');
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const handleTimeOptionChange = (value: TableTimeFormat | undefined) => {
    if (!value) return;
    updateTableConfig({ timeFormat: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save time format preference' });
    });
  };
  // Snapshot initial URL state for pagination so we don't read the cursor
  // off every re-render once the hook owns it.
  const initialPagination = useRef({
    cursor: searchParams.get('starting_after') ?? undefined,
    pageIndex: Number(searchParams.get('page_index')) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    setPageSize,
    pageIndex,
    hasPrevious,
  } = usePaginationState({
    defaultPageSize: pageSize,
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    updateTableConfig({ limit: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save page size preference' });
    });
  };

  const isCounterpartySearchEnabled = useFeatureFlag(FeatureFlag.EnableCounterpartySearch);

  const [filters, setFilters] = useState<CounterpartyFilters>(() => filtersFromSearchParams(searchParams));
  const [queryInput, setQueryInput] = useState(() => filtersFromSearchParams(searchParams).query);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  useEffect(() => {
    const params = filtersToSearchParams(filters);
    if (cursor) params.set('starting_after', cursor);
    if (pageIndex > 0) params.set('page_index', String(pageIndex));
    setSearchParams(params, { replace: true });
  }, [filters, cursor, pageIndex, setSearchParams]);

  const updateFilter = <K extends keyof CounterpartyFilters>(key: K, value: CounterpartyFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Local debounce so Reset can cancel any pending query update.
  const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingQuery = useCallback(() => {
    if (queryDebounceRef.current) {
      clearTimeout(queryDebounceRef.current);
      queryDebounceRef.current = null;
    }
  }, []);
  useEffect(() => cancelPendingQuery, [cancelPendingQuery]);

  const handleQueryInputChange = (value: string) => {
    setQueryInput(value);
    cancelPendingQuery();
    queryDebounceRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, query: value }));
    }, 300);
  };

  const handleFilterReset = () => {
    cancelPendingQuery();
    // Preserve sort and deduplicate; Reset only clears filter-style fields.
    setFilters((prev) => ({ ...EMPTY_FILTERS, sort: prev.sort, deduplicate: prev.deduplicate }));
    setQueryInput('');
  };

  const isDateFilterActive = Boolean(filters.date.createdAtGte || filters.date.createdAtLt);

  const isFilterActive =
    Boolean(filters.accountNumber) || Boolean(filters.routingNumber) || Boolean(filters.query) || isDateFilterActive;

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      const threshold = isFilterActive ? 1200 : 1000;
      setIsSmallViewport(window.innerWidth < threshold);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFilterActive]);

  const query = useMemo(
    () => buildQuery(filters, isCounterpartySearchEnabled, pageSize, cursor),
    [filters, isCounterpartySearchEnabled, pageSize, cursor]
  );

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const {
    data: counterpartiesData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...counterpartyListCounterpartiesOptions({ query }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const counterparties = useMemo<Counterparty[]>(() => counterpartiesData?.counterparties ?? [], [counterpartiesData]);
  const hasMore = counterpartiesData?.has_more ?? false;

  const showEnterHint = !isFetching && filters.query === queryInput && counterparties.length === 1 && !hasMore;

  const handleQuerySubmit = (value: string) => {
    if (showEnterHint) {
      navigate(`${ROUTE.COUNTERPARTIES}/edit/${counterparties[0].id}`);
      return;
    }
    setQueryInput(value);
    cancelPendingQuery();
    setFilters((prev) => ({ ...prev, query: value }));
  };

  const handleDelete = (entry: Counterparty) => {
    openAlert({
      headline: 'Delete counterparty',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        counterpartyDeleteCounterparty({ path: { counterparty_id: entry.id }, throwOnError: true })
          .then(() => {
            addSuccessNotification({ content: 'Counterparty deleted' });
            refetch();
          })
          .catch((error) => {
            addDangerNotification({ content: error?.message ?? 'Failed to delete counterparty' });
          });
      },
    });
  };

  const columns: TableColumn<Counterparty>[] = [
    {
      id: 'description',
      header: 'Description / Name',
      width: { min: 300, max: '2fr' },
      cell: ({ row }) => <Table.Cell>{counterpartyDisplayName(row)}</Table.Cell>,
    },
    {
      id: 'routing_number',
      header: 'Routing Number',
      width: { min: 'min-content', max: '0.5fr' },
      cell: ({ row }) => (
        <Table.Cell>
          <Truncate>{row.routing_number}</Truncate>
        </Table.Cell>
      ),
    },
    {
      id: 'account_number',
      header: 'Account Number',
      width: { min: 'min-content', max: '0.7fr' },
      cell: ({ row }) => (
        <Table.Cell>
          <Truncate>{row.account_number}</Truncate>
        </Table.Cell>
      ),
    },
    {
      id: 'created_at',
      header: (
        <Dropdown
          size="small"
          positionRight
          active={timeFormat}
          options={[
            { label: 'Relative Time', value: TableTimeFormat.Relative },
            { label: 'Date / Time', value: TableTimeFormat.Absolute },
          ]}
          onChange={handleTimeOptionChange}
          customLabel={
            <Flex alignItems="center" gap={4}>
              Created <Icon.ChevronDown />
            </Flex>
          }
        />
      ),
      width: { fixed: 'min-content' },
      cell: ({ row }) =>
        row.created_at ? (
          <CreatedTimeCell>
            {timeFormat === TableTimeFormat.Relative ? (
              <RelativeTime alignment="right" timestamp={new Date(row.created_at)} />
            ) : (
              <AbsoluteTime alignment="left" timestamp={new Date(row.created_at)} />
            )}
          </CreatedTimeCell>
        ) : (
          <Table.Cell />
        ),
    },
  ];

  return (
    <>
      <PageHeader text="Counterparties" border={false}>
        <Button onClick={() => navigate(ROUTE.BANK_ACCOUNTS)} icon={<Icon.Bank />} size="small" variant="secondary">
          Accounts
        </Button>
        <Button onClick={() => navigate(`${ROUTE.COUNTERPARTIES}/edit`)} icon={<Icon.Plus />} size="small">
          Create Counterparty
        </Button>
      </PageHeader>

      <Toolbar>
        <ToolbarGroup>
          {isSmallViewport ? (
            <>
              <Button size="small" variant="secondary" icon={<Icon.Filter />} onClick={() => setIsFiltersOpen(true)}>
                Filters
              </Button>
              {isFilterActive && (
                <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                  Reset
                </ResetFilter>
              )}
            </>
          ) : (
            <>
              <PopoverFilterWrapper
                label="Routing Number"
                isActive={Boolean(filters.routingNumber)}
                onSubmit={(value: string | undefined) => updateFilter('routingNumber', value ?? '')}
              >
                <PopoverFilterText label="Routing Number" placeholder="Routing number" value={filters.routingNumber} />
              </PopoverFilterWrapper>
              <PopoverFilterWrapper
                label="Account Number"
                isActive={Boolean(filters.accountNumber)}
                onSubmit={(value: string | undefined) => updateFilter('accountNumber', value ?? '')}
              >
                <PopoverFilterText label="Account Number" placeholder="Account number" value={filters.accountNumber} />
              </PopoverFilterWrapper>
              <PopoverFilterWrapper
                label="Created"
                isActive={isDateFilterActive}
                onSubmit={(data: Partial<PopoverFilterTransferDateData>) => updateFilter('date', data)}
              >
                <PopoverFilterCreatedDate data={filters.date} convertDateRange={convertOptionalDatesToRange} />
              </PopoverFilterWrapper>
              <CounterpartySearch
                value={queryInput}
                placeholder={isCounterpartySearchEnabled ? 'Search counterparties...' : 'Search by description...'}
                onValueChange={handleQueryInputChange}
                onFilterSubmit={handleQuerySubmit}
                showEnterHint={showEnterHint}
                width={250}
              />
              {isFilterActive && (
                <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                  Reset
                </ResetFilter>
              )}
            </>
          )}
        </ToolbarGroup>
        {isCounterpartySearchEnabled && (
          <ToolbarGroup>
            <Tooltip content="Group counterparties sharing the same routing and account number." placement="left">
              <DeduplicateToggle>
                <DeduplicateToggleLabel>Deduplicate</DeduplicateToggleLabel>
                <Toggle
                  isChecked={filters.deduplicate === 'true'}
                  onCheckedChange={(value) => updateFilter('deduplicate', value ? 'true' : 'false')}
                />
              </DeduplicateToggle>
            </Tooltip>
            <CompactFilterDropdown
              size="small"
              positionRight
              active={filters.sort}
              $isFilterActive={Boolean(filters.sort)}
              options={SORT_OPTIONS}
              label={SORT_OPTIONS.find((o) => o.value === filters.sort)?.label ?? 'Sort'}
              onChange={(value: SortSelection) => updateFilter('sort', value)}
            />
          </ToolbarGroup>
        )}
      </Toolbar>

      <Inner pt={0} px="var(--page-margin)">
        {!isLoading && counterparties.length === 0 && !isFilterActive ? (
          <EmptyState headline="No counterparties found" text="Add your first counterparty to get started.">
            <Button onClick={() => navigate(`${ROUTE.COUNTERPARTIES}/edit`)} size="small" icon={<Icon.Plus />}>
              Create Counterparty
            </Button>
          </EmptyState>
        ) : (
          <Table<Counterparty>
            columns={columns}
            data={counterparties}
            isLoading={isLoading || isFetching}
            variant={TableVariant.List}
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (counterparties.length > 0) {
                  onNext({ cursor: counterparties[counterparties.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: handlePageSizeChange,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.COUNTERPARTIES}/edit/${row.id}`);
            }}
            rowAction={(row) => (
              <StyledDropdown
                isInside
                positionRight
                options={[
                  {
                    label: 'View',
                    onClick: (event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      navigate(`${ROUTE.COUNTERPARTIES}/edit/${row.id}`);
                    },
                  },
                  {
                    label: 'Delete',
                    isDanger: true,
                    onClick: (event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      handleDelete(row);
                    },
                  },
                ]}
                variant="dots"
              />
            )}
          />
        )}
      </Inner>

      <CounterpartyFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        showSearch={isCounterpartySearchEnabled}
        initialAccountNumber={filters.accountNumber}
        initialRoutingNumber={filters.routingNumber}
        initialQuery={filters.query}
        initialDate={filters.date}
        convertDateRange={convertOptionalDatesToRange}
        onApply={(values) => {
          setFilters((prev) => ({
            ...prev,
            accountNumber: values.accountNumber,
            routingNumber: values.routingNumber,
            query: values.query,
            date: values.date,
          }));
          setQueryInput(values.query);
        }}
      />
    </>
  );
};
